import _ from 'lodash'

/**
 * @typedef {Object} IgnoreSettings
 *
 * @property {String[]} [noFetch] - list of attributes for which a change
 * should not trigger a fetch of the chart data
 *
 * @property {String[]} [noRerender] - list of attributes for which a change
 * should not trigger a re-render of the chart. Any attributes in here are
 * implied to be noFetch attributes, too (no reason to fetch if no re-render)
 *
 * @property {Object<String, IgnoreSettings[]>} [nested] - key is an attribute name,
 * and value is IgnoreSettings for any nested attributes. Works whether the attribute
 * is an object or an array of objects.
 */

/**
 * Compares the old and current state of a chart designer and determines if a
 * fetch and/or rerender is needed.
 *
 * @param {Object} current - the current state of the chart designer in POJO form
 * (not the ChartDesigner Model)
 *
 * @param {Object} previous - the previous state of the chart designer in POJO form
 *
 * @param {IgnoreSettings} [ignoreSettings] - settings for any attributes whose
 * changes should not trigger fetch/re-render. See IgnoreSettings for details.
 *
 * @returns {{fetch: (boolean), rerender: (boolean)}} - if fetch==true, the
 * chart data should be re-fetched. If rerender===true, the chart should be
 * re-rendered
 */
function updatesNeeded (current, previous, ignoreSettings) {
	let [ nestedAttrs, regularAttrs ] = _(current).keys().partition(attr => {
		return _(ignoreSettings.nested).keys().includes(attr)
	})

	let rerender = false
	let fetch = false

	// First check any attributes with corresponding nested IgnoreSettings, since
	// a rerender or fetch result from them would override the "basic" attributes
	_(ignoreSettings.nested).toPairs().forEach(([nestedAttr, nestedOptions]) => {
		let nestedCurrentArray = toArray(current[nestedAttr])
		let nestedPreviousArray = toArray(previous[nestedAttr])

		// If there are a different number of nested objects, then assume it's
		// a significant enough change to require fetch/rerender
		if (nestedCurrentArray.length != nestedPreviousArray.length) {
			rerender = true
			fetch = true
			return
		}

		_.forEach(nestedCurrentArray, (nestedCurrent, i) => {
			let nestedPrevious = nestedPreviousArray[i]

			let nestedResults = updatesNeeded(
				nestedCurrent,
				nestedPrevious,
				nestedOptions)

			if (nestedResults.fetch) {
				rerender = true
				fetch = true
			} else if (nestedResults.rerender) {
				rerender = true
			}
		})
	})

	if (rerender || fetch) {
		return { rerender, fetch }
	}

	// Now check the basic non-nested attributes
	let nonNestedCurrent = _.pick(current, regularAttrs)
	let nonNestedPrevious = _.pick(previous, regularAttrs)

	const DEFAULT = []

	// Any noRerender attributes are implied to be noFetch (no reason to fetch
	// if there isn't a re-render)
	let noFetch = _.concat(ignoreSettings.noRerender || DEFAULT, ignoreSettings.noFetch || DEFAULT)

	if (onlyChanged(nonNestedCurrent, nonNestedPrevious, noFetch)) {
		// If the only changes were to noFetch attributes, then see if we need
		// to rerender
		if (onlyChanged(nonNestedCurrent, nonNestedPrevious, ignoreSettings.noRerender || DEFAULT)) {
			// All changes were no noRerender attributes, so no need to fetch
			// or rerender
			return { rerender: false, fetch: false }
		} else {
			// At least one change was to a noFetch attribute that wasn't also
			// a noRerender attribute, so while we can skip the fetch, we need
			// to rerender
			return { fetch: false, rerender: true }
		}
	} else {
		// If there were any changes to attributes that aren't noFetch/noRerender,
		// then we need to fetch and rerender
		return { fetch: true, rerender: true }
	}

	return { rerender, fetch }
}

/**
 * Returns the value in array form. If it's already an array, just returns it.
 * If it's a nil value, returns an empty array. Any other non-array, non-nil
 * value is returned as a single-item array
 */
function toArray (value) {
	if (_.isArray(value)) {
		return value
	}

	return _.isNil(value) ? [] : [value]
}

/**
 * Returns true if nothing changed between the two objects (chart or series)
 * or only the "ignored" attributes changed
 */
function onlyChanged(current, previous, attributes) {
	// find out everything that changed, ignoring the given 'attributes'
	let changed = _(halfDiff(current, previous))
		.concat(halfDiff(previous, current))
		.uniq()
		.filter(k => !_.includes(attributes, k)).value()

	return changed.length === 0
}

// Returns any attributes from object "a" that are different in object "b". Note
// that this won't catch attributes that are in "b", but not "a" (hence "halfDiff")
function halfDiff(a, b) {
	return _.reduce(a, function(result, value, key) {
		return _.isEqual(value, b[key]) ?
			result : result.concat(key)
	}, [])
}

export default updatesNeeded

updatesNeeded.__filename__ = 'charts/edit/updates-needed.js'
